import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ExportButton from '../components/ExportButton';
import VoiceSearchButton from '../components/VoiceSearchButton';
import api from '../api';
import { useAuth } from '../context/AuthContext';

export default function Loans() {
  const { user } = useAuth();
  const isAdmin = user && user.role === 'admin';
  const [loans, setLoans] = useState([]);
  const [filter, setFilter] = useState('active');
  // Click-to-sort, as requested.
  const [sort, setSort] = useState({ key: 'loan_date', dir: 'desc' });
  // Search bar: same look as the Customers screen search box, but filters
  // the already-loaded loans list instantly on every keystroke instead of
  // submitting to the server, per the request.
  const [search, setSearch] = useState('');

  useEffect(() => { fetchLoans(); }, [filter]);

  const fetchLoans = () => {
    api.get('/loans?status=' + filter).then(r => setLoans(r.data)).catch(console.error);
  };

  const handleDelete = (id, customerName) => {
    if (!window.confirm('Undo loan for ' + customerName + '? This reverses all transactions (within 7 days).')) return;
    api.delete('/loans/' + id).then(() => { fetchLoans(); alert('Loan reversed!'); }).catch(err => alert(err.response?.data?.error || 'Cannot undo'));
  };

  const fmt = (n) => '\u20B9' + (n || 0).toLocaleString('en-IN');

  const columns = [
    { key: 'id', label: 'Loan #' },
    { key: 'customer_name', label: 'Customer' },
    { key: 'loan_amount', label: 'Amount' },
    { key: 'interest_rate', label: 'Rate' },
    { key: 'frequency', label: 'Freq' },
    { key: 'loan_date', label: 'Date' },
    { key: 'account_name', label: 'Account' },
  ];

  function handleSort(key) {
    setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  }
  function sortIndicator(key) {
    if (sort.key !== key) return '';
    return sort.dir === 'asc' ? ' \u25B2' : ' \u25BC';
  }

  const filteredLoans = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return loans;
    return loans.filter(l =>
      (l.customer_name || '').toLowerCase().includes(q) ||
      (l.phone1 || '').includes(q) ||
      (l.phone2 || '').includes(q)
    );
  })();

  const sortedLoans = filteredLoans.slice().sort((a, b) => {
    let av = a[sort.key], bv = b[sort.key];
    if (typeof av === 'number' || typeof bv === 'number') { av = av || 0; bv = bv || 0; }
    else { av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase(); }
    if (av < bv) return sort.dir === 'asc' ? -1 : 1;
    if (av > bv) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 'bold', margin: 0 }}>Loans</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Link to="/loans/new" style={{ background: '#16a34a', color: 'white', padding: '8px 16px', borderRadius: 8, textDecoration: 'none' }}>+ New Loan</Link>
          <ExportButton apiUrl="/api/loans" filename="loans.csv" />
        </div>
      </div>
      {/* Same search bar markup as the Customers screen, reused as-is. */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by customer name or phone..." style={{ flex: 1, padding: 10, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }} />
          <VoiceSearchButton onResult={(transcript) => setSearch(transcript)} />
        </div>
        {search && <button type="button" onClick={() => setSearch('')} style={{ marginTop: 6, padding: 0, background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13 }}>✕ Clear search</button>}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['active', 'closed'].map(s => (
          <button key={s} onClick={() => setFilter(s)} style={{
            padding: '8px 16px', borderRadius: 8, fontWeight: 500, textTransform: 'capitalize', border: 'none', cursor: 'pointer',
            background: filter === s ? '#2563eb' : 'white', color: filter === s ? 'white' : '#4b5563'
          }}>{s}</button>
        ))}
      </div>
      <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {columns.map(col => (
                <th key={col.key} onClick={() => handleSort(col.key)} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 500, color: sort.key === col.key ? '#2563eb' : '#6b7280', textTransform: 'uppercase', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>{col.label}{sortIndicator(col.key)}</th>
              ))}
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 500, color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedLoans.map(l => (
              <tr key={l.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '12px 16px', fontWeight: 500 }}>#{l.id}</td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ fontWeight: 500 }}>{l.customer_name}</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>{l.client_number}</div>
                </td>
                <td style={{ padding: '12px 16px' }}>{fmt(l.loan_amount)}</td>
                <td style={{ padding: '12px 16px' }}>{l.interest_rate}%</td>
                <td style={{ padding: '12px 16px', textTransform: 'capitalize' }}>{l.frequency}</td>
                <td style={{ padding: '12px 16px' }}>{new Date(l.loan_date).toLocaleDateString('en-IN')}</td>
                <td style={{ padding: '12px 16px' }}>{l.account_name}</td>
                <td style={{ padding: '12px 16px' }}>
                  <div className="action-links" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Link to={'/loans/' + l.id} style={{ color: '#2563eb', textDecoration: 'none' }}>View</Link>
                    {/* Undo is admin-only at your request; also enforced
                        server-side (DELETE /api/loans/:id requires admin)
                        so it can't be reached by calling the API directly. */}
                    {isAdmin && l.status === 'active' && <button onClick={() => handleDelete(l.id, l.customer_name)} style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Undo</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loans.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}>No {filter} loans</div>}
      </div>
    </div>
  );
}
